/**
 * Order Detail Page - Tailwind Version
 */

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  User,
  Truck,
  CreditCard,
  RefreshCw,
  Check,
  X,
  Package,
  Phone,
  Mail,
  MapPin,
  Clock,
  ChevronDown,
  ShoppingBag,
  Bike,
} from "lucide-react";
import dayjs from "dayjs";
import "dayjs/locale/de";

import {
  Order,
  OrderDish,
  OrderStatus,
  User as UserType,
  DeliveryMethod,
} from "@tasty/api";
import { OrderClient, OrderDishClient, UserClient } from "@tasty/api/client";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { cn, formatOrderId, formatPrice } from "../../lib/utils";
import { TERMS } from "@tasty/shared";
import { toast } from "sonner";
import getFees from "../../utils/get-fees";

dayjs.locale("de");

// Status Config
const STATUS_CONFIG: Record<
  OrderStatus,
  { label: string; color: string; bgColor: string }
> = {
  [OrderStatus.PENDING]: {
    label: "Ausstehend",
    color: "text-slate-700",
    bgColor: "bg-slate-100",
  },
  [OrderStatus.NEW]: {
    label: "Neu",
    color: "text-blue-700",
    bgColor: "bg-blue-100",
  },
  [OrderStatus.ACCEPTED]: {
    label: "Angenommen",
    color: "text-teal-700",
    bgColor: "bg-teal-100",
  },
  [OrderStatus.COOKING]: {
    label: "In Zubereitung",
    color: "text-orange-700",
    bgColor: "bg-orange-100",
  },
  [OrderStatus.READY_FOR_PICKUP]: {
    label: "Bereit",
    color: "text-green-700",
    bgColor: "bg-green-100",
  },
  [OrderStatus.PICKED_UP]: {
    label: "Unterwegs",
    color: "text-purple-700",
    bgColor: "bg-purple-100",
  },
  [OrderStatus.COMPLETED]: {
    label: "Abgeschlossen",
    color: "text-gray-700",
    bgColor: "bg-gray-100",
  },
  [OrderStatus.DECLINED_BY_RESTAURANT]: {
    label: "Abgelehnt",
    color: "text-red-700",
    bgColor: "bg-red-100",
  },
};

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [order, setOrder] = useState<Order | null>(null);
  const [customer, setCustomer] = useState<UserType | null>(null);
  const [orderDishes, setOrderDishes] = useState<OrderDish[]>([]);

  // Expanded sections
  const [customerExpanded, setCustomerExpanded] = useState(true);
  const [deliveryExpanded, setDeliveryExpanded] = useState(false);
  const [costExpanded, setCostExpanded] = useState(false);

  const fetchData = async () => {
    if (!id) return;

    try {
      setLoading(true);

      // Fetch Order
      const theOrder = await OrderClient.queryItem(id);
      if (!theOrder) {
        toast.error("Bestellung nicht gefunden");
        navigate("/");
        return;
      }
      setOrder(theOrder);

      // Fetch Order Dishes
      const dishes = await OrderDishClient.queryListByOrderID({ orderID: id });
      setOrderDishes(dishes || []);

      // Fetch Customer
      if (theOrder.userID) {
        const user = await UserClient.queryItem({ id: theOrder.userID });
        setCustomer(user);
      }
    } catch (error) {
      console.error("Error fetching order:", error);
      toast.error("Fehler beim Laden der Bestellung");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (newStatus: OrderStatus) => {
    if (!order) return;

    setUpdating(true);
    try {
      await OrderClient.updateItem({ id: order.id, status: newStatus });
      setOrder({ ...order, status: newStatus });
      toast.success("Bestellung aktualisiert");
    } catch (error) {
      console.error("Error updating order:", error);
      toast.error("Fehler beim Aktualisieren");
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  // Global Refresh Button (Header) -> Page refresh
  useEffect(() => {
    const handler = () => {
      fetchData();
    };
    window.addEventListener("dashboard-refresh", handler as EventListener);
    return () =>
      window.removeEventListener("dashboard-refresh", handler as EventListener);
  }, [id]);

  // Subscribe to updates
  useEffect(() => {
    if (!id) return;

    const subscription = OrderClient.subscribeToUpdates({
      filter: { id: { eq: id } },
    }).subscribe({
      next: ({ value }) => {
        if (value?.data?.onUpdateOrder) {
          setOrder(value.data.onUpdateOrder as Order);
          toast.info("Bestellung wurde aktualisiert");
        }
      },
      error: (error) => console.error("Subscription error:", error),
    });

    return () => subscription.unsubscribe();
  }, [id]);

  const statusConfig = order
    ? STATUS_CONFIG[order.status] || STATUS_CONFIG.NEW
    : STATUS_CONFIG.NEW;

  const fees = order
    ? getFees(order.createdAt, order.deliveryMethod, order.deliveryProvider)
    : { deliveryFee: 0, customerFee: 0, restaurantFee: 0 };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Bestellung nicht gefunden</p>
        <Button
          variant="outline"
          onClick={() => navigate("/")}
          className="mt-4"
        >
          Zurück zur Übersicht
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück
          </Button>
          <div>
            <p className="text-sm font-medium text-foreground">
              Bestellung {formatOrderId(order.id)}
            </p>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-3 w-3" />
              {dayjs(order.createdAt).format("DD.MM.YYYY HH:mm")}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge className={cn(statusConfig.bgColor, statusConfig.color)}>
            {statusConfig.label}
          </Badge>
        </div>
      </div>

      {/* Order Note */}
      {order.note && (
        <div className="p-4 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-lg">
          <p className="font-semibold text-orange-800 dark:text-orange-200">
            Anmerkung zur Bestellung:
          </p>
          <p className="text-orange-700 dark:text-orange-300 italic mt-1">
            {order.note}
          </p>
        </div>
      )}

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Order Items */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <h2 className="font-semibold">Bestellte {TERMS.productPlural}</h2>
            </CardHeader>
            <CardContent className="divide-y">
              {orderDishes.length === 0 ? (
                <p className="text-muted-foreground py-4">
                  Keine {TERMS.productPlural} gefunden
                </p>
              ) : (
                orderDishes.map((dish) => (
                  <div key={dish.id} className="py-3 flex justify-between">
                    <div>
                      <p className="font-medium">
                        {dish.quantity}x {dish.Dish?.name || TERMS.product}
                      </p>
                      {dish.note && (
                        <p className="text-sm text-orange-600 mt-1">
                          💬 {dish.note}
                        </p>
                      )}
                    </div>
                    <p className="font-semibold">
                      {formatPrice((dish.Dish?.price ?? 0) * dish.quantity)}
                    </p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-3">
                {order.status === OrderStatus.NEW && (
                  <>
                    <Button
                      variant="destructive"
                      disabled={updating}
                      onClick={() =>
                        updateStatus(OrderStatus.DECLINED_BY_RESTAURANT)
                      }
                    >
                      <X className="h-4 w-4 mr-2" />
                      Ablehnen
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      disabled={updating}
                      onClick={() => updateStatus(OrderStatus.COOKING)}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Annehmen
                    </Button>
                  </>
                )}

                {order.status === OrderStatus.COOKING && (
                  <Button
                    className="bg-green-600 hover:bg-green-700"
                    disabled={updating}
                    onClick={() => updateStatus(OrderStatus.READY_FOR_PICKUP)}
                  >
                    <Package className="h-4 w-4 mr-2" />
                    Bereit zur Abholung
                  </Button>
                )}

                {order.status === OrderStatus.READY_FOR_PICKUP &&
                  order.deliveryMethod === DeliveryMethod.PICKUP && (
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      disabled={updating}
                      onClick={() => updateStatus(OrderStatus.COMPLETED)}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Abgeholt
                    </Button>
                  )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Info Sections */}
        <div className="space-y-4">
          {/* Customer Info */}
          <Card>
            <button
              className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
              onClick={() => setCustomerExpanded(!customerExpanded)}
            >
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-muted-foreground" />
                <span className="font-semibold">Kundeninformationen</span>
              </div>
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform",
                  customerExpanded && "rotate-180"
                )}
              />
            </button>
            {customerExpanded && (
              <CardContent className="pt-0 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>{customer?.name || "–"}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{customer?.phone || "–"}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>
                    {customer?.address ||
                      (customer?.addressData as any)?.formattedAddress ||
                      "–"}
                  </span>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Delivery Info */}
          <Card>
            <button
              className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
              onClick={() => setDeliveryExpanded(!deliveryExpanded)}
            >
              <div className="flex items-center gap-2">
                {order.deliveryMethod === DeliveryMethod.DELIVERY ? (
                  <Bike className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ShoppingBag className="h-5 w-5 text-muted-foreground" />
                )}
                <span className="font-semibold">Lieferinformationen</span>
              </div>
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform",
                  deliveryExpanded && "rotate-180"
                )}
              />
            </button>
            {deliveryExpanded && (
              <CardContent className="pt-0 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Liefermethode</span>
                  <span className="font-medium">{order.deliveryMethod}</span>
                </div>
                {order.deliveryMethod !== DeliveryMethod.PICKUP && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Lieferdienst</span>
                    <span className="font-medium">
                      {order.deliveryProvider}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Liefergebühr</span>
                  <span className="font-medium">
                    {formatPrice(fees.deliveryFee)}
                  </span>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Cost Info */}
          <Card>
            <button
              className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
              onClick={() => setCostExpanded(!costExpanded)}
            >
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-muted-foreground" />
                <span className="font-semibold">Kostenübersicht</span>
              </div>
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform",
                  costExpanded && "rotate-180"
                )}
              />
            </button>
            {costExpanded && (
              <CardContent className="pt-0 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Kunde bezahlte</span>
                  <span className="font-bold text-lg">
                    {formatPrice(order.total || 0)}
                  </span>
                </div>
                {order.voucherAmountOff && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Ermäßigung</span>
                    <span className="font-medium text-green-600">
                      -{formatPrice(order.voucherAmountOff / 100)} (
                      {order.voucherCode})
                    </span>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
